/**
 * Which rail tools each onboarding role starts with.
 *
 * The owner adjusts these; they live in this one file on purpose (plan §1).
 * A role never DISABLES anything: it only decides which rail buttons are
 * hidden (`railHidden` in the extensions store). Disabling an extension tears
 * down its panels, and Collections/History are built-ins with no switch at all.
 *
 * TOOLS is in rail order. `footer` marks the two tools pinned to the bottom
 * group of the rail (ActivityBar FOOTER_ITEM_IDS). `video` is the CDN clip id
 * shown as a tour step when the tool is kept (plan §3); null = no tour step.
 */
import {
  Send,
  Clock,
  GitBranch,
  Database,
  Terminal,
  Server,
  Mail,
  FolderKanban,
  Kanban,
  FileText,
  Lock,
  Network,
  MessageSquare,
  Calendar,
  CodeXml,
  ClipboardList,
  FlaskConical,
  ServerCog,
  SlidersHorizontal,
} from 'lucide-react';
import { DESKTOP_ONLY_ACTIVITY_IDS, isWebApp } from '../platform/target';

export const TOOLS = Object.freeze([
  { id: 'collections', key: 'collections', Icon: Send, video: 'requests' },
  { id: 'history', key: 'history', Icon: Clock, video: null },
  { id: 'git', key: 'git', Icon: GitBranch, video: null },
  { id: 'db-manager.activity', key: 'dbManager', Icon: Database, video: 'db-manager' },
  { id: 'terminal.activity', key: 'terminal', Icon: Terminal, video: 'terminal' },
  { id: 'ftp.activity', key: 'ftp', Icon: Server, video: 'ftp' },
  { id: 'mail-catcher.activity', key: 'mailCatcher', Icon: Mail, video: 'mail-catcher' },
  { id: 'projects.activity', key: 'projects', Icon: FolderKanban, video: null },
  { id: 'task-manager.activity', key: 'boards', Icon: Kanban, video: 'boards' },
  { id: 'documents.activity', key: 'documents', Icon: FileText, video: 'documents' },
  { id: 'vault.activity', key: 'vault', Icon: Lock, video: null },
  { id: 'graph.activity', key: 'graph', Icon: Network, video: null },
  { id: 'chat.activity', key: 'chat', Icon: MessageSquare, video: 'chat', footer: true },
  { id: 'calendar.activity', key: 'calendar', Icon: Calendar, video: null, footer: true },
]);

export const ALL_TOOL_IDS = Object.freeze(TOOLS.map((t) => t.id));

const PM = ['projects.activity', 'task-manager.activity', 'documents.activity', 'vault.activity', 'graph.activity', 'chat.activity', 'calendar.activity'];
const QA = [
  'collections', 'history', 'db-manager.activity', 'mail-catcher.activity',
  'projects.activity', 'task-manager.activity', 'documents.activity', 'vault.activity',
  'chat.activity', 'calendar.activity',
];
const DEVOPS = [
  'collections', 'history', 'git', 'db-manager.activity', 'terminal.activity', 'ftp.activity',
  'projects.activity', 'task-manager.activity', 'documents.activity', 'vault.activity',
  'chat.activity', 'calendar.activity',
];

export const ROLES = Object.freeze([
  { id: 'developer', Icon: CodeXml, tools: ALL_TOOL_IDS },
  { id: 'pm', Icon: ClipboardList, tools: PM },
  { id: 'qa', Icon: FlaskConical, tools: QA },
  { id: 'devops', Icon: ServerCog, tools: DEVOPS },
  { id: 'custom', Icon: SlidersHorizontal, tools: ALL_TOOL_IDS },
]);

export function roleById(id) {
  return ROLES.find((r) => r.id === id) || null;
}

export function toolById(id) {
  return TOOLS.find((t) => t.id === id) || null;
}

/** The rail ids a role recommends (keeps). Unknown role = every tool. */
export function recommendedFor(roleId) {
  const role = roleById(roleId);
  return role ? [...role.tools] : [...ALL_TOOL_IDS];
}

/** The tools this build can put on the rail (the web app has no devtools). */
export function availableTools() {
  const web = isWebApp();
  return TOOLS.filter((tool) => !(web && DESKTOP_ONLY_ACTIVITY_IDS.has(tool.id)));
}

/** The rail ids a role HIDES: every tool it does not recommend. */
export function hiddenForRole(roleId) {
  const keep = new Set(recommendedFor(roleId));
  return ALL_TOOL_IDS.filter((id) => !keep.has(id));
}
